import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, catchError, finalize, forkJoin, of, takeUntil } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { Loading } from '@components/ui/loading/loading';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { MsfInput } from '@components/ui/forms/input/input';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';
import { AssignmentProcessService as AssignmentProcessApiService } from '@6grain/masofa-monolith-api-client/common/api/assignmentProcess.service';
import { OrchestrationResourceProfileService as OrchestrationResourceProfileApiService } from '@6grain/masofa-monolith-api-client/common/api/orchestrationResourceProfile.service';
import { OrchestrationResourceProfileResponse } from '@6grain/masofa-monolith-api-client/common/model/orchestrationResourceProfileResponse';
import { QueueParallelismRequest } from '@6grain/masofa-monolith-api-client/common/model/queueParallelismRequest';
import { SaveOrchestrationResourceProfileRequest } from '@6grain/masofa-monolith-api-client/common/model/saveOrchestrationResourceProfileRequest';
import {
  ProcessCapCatalogItem,
  ProcessCapRow,
  buildProcessCapRows,
  formatProcessConcurrencySummary,
  overlayFromCapRows,
} from './orchestration-resources-page.util';

type RequiredFields<T, K extends keyof T> = T & { [P in K]-?: NonNullable<T[P]> };
type QueueParallelismForm = RequiredFields<QueueParallelismRequest, keyof QueueParallelismRequest>;
type ResourceProfileForm = RequiredFields<
  SaveOrchestrationResourceProfileRequest,
  Exclude<keyof SaveOrchestrationResourceProfileRequest, 'processConcurrency'>
> & {
  downloadQueues: QueueParallelismForm;
  postProcessQueues: QueueParallelismForm;
  processConcurrency: { [key: string]: number };
};
type ResourceProfile = RequiredFields<OrchestrationResourceProfileResponse,
  'id' | 'key' | 'name' | 'downloadQueues' | 'postProcessQueues'> & {
  assignmentCapSummary: string;
};

@Component({
  selector: 'app-orchestration-resources-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    TagModule,
    Loading,
    OrchestrationTableHeadingComponent,
    CopyableIdentifierComponent,
    PrimaryButton,
    MsfInput,
  ],
  templateUrl: './orchestration-resources-page.html',
  styleUrl: './orchestration-resources-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationResourcesPageComponent implements OnInit, OnDestroy {
  private readonly service = inject(OrchestrationResourceProfileApiService);
  private readonly processesApi = inject(AssignmentProcessApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  profiles: ResourceProfile[] = [];
  private allProfiles: ResourceProfile[] = [];
  private processCatalog: ProcessCapCatalogItem[] = [];
  capRows: ProcessCapRow[] = [];
  searchQuery = '';
  emptyListMessage = 'No resource profiles found';
  editing: ResourceProfile | null = null;
  form = this.defaultForm();
  loading = false;
  saving = false;
  dialogVisible = false;
  errorMessage = '';

  ngOnInit(): void { this.load(); }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCatalogSearch(query: string): void {
    this.searchQuery = query ?? '';
    this.applyCatalogSearch();
    this.cdr.markForCheck();
  }

  load(): void {
    this.loading = true;
    this.errorMessage = '';
    forkJoin({
      profiles: this.service.orchestrationResourceProfilesGet(),
      processes: this.processesApi.assignmentsProcessesGet().pipe(catchError(() => of([]))),
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => { this.loading = false; this.cdr.markForCheck(); }),
    ).subscribe({
      next: ({ profiles, processes }) => {
        this.processCatalog = (processes as ProcessCapCatalogItem[]) ?? [];
        this.allProfiles = ((profiles as OrchestrationResourceProfileResponse[]) ?? []).map(profile => ({
          ...(profile as ResourceProfile),
          assignmentCapSummary: formatProcessConcurrencySummary(profile.processConcurrency),
        }));
        this.applyCatalogSearch();
      },
      error: () => { this.errorMessage = 'Failed to load resource profiles'; },
    });
  }

  create(): void {
    this.editing = null;
    this.form = this.defaultForm();
    this.capRows = buildProcessCapRows(this.processCatalog, {});
    this.errorMessage = '';
    this.dialogVisible = true;
  }

  edit(profile: ResourceProfile): void {
    this.editing = profile;
    this.form = {
      key: profile.key,
      name: profile.name,
      isActive: profile.isActive ?? false,
      copernicusDownloadConcurrency: profile.copernicusDownloadConcurrency ?? 1,
      minioReadConcurrency: profile.minioReadConcurrency ?? 1,
      minioWriteConcurrency: profile.minioWriteConcurrency ?? 1,
      gdalConcurrency: profile.gdalConcurrency ?? 1,
      gdalCacheMaxMB: profile.gdalCacheMaxMB ?? 256,
      downloadQueues: this.normalizeQueues(profile.downloadQueues),
      postProcessQueues: this.normalizeQueues(profile.postProcessQueues),
      processConcurrency: profile.processConcurrency ?? {},
    };
    this.capRows = buildProcessCapRows(this.processCatalog, this.form.processConcurrency);
    this.errorMessage = '';
    this.dialogVisible = true;
  }

  onCapInput(row: ProcessCapRow, value: unknown): void {
    row.capInput = value == null ? '' : String(value);
    this.cdr.markForCheck();
  }

  save(): void {
    const parsed = overlayFromCapRows(this.capRows);
    if (parsed.invalid) {
      this.errorMessage = 'Invalid process concurrency cap';
      this.cdr.markForCheck();
      return;
    }
    this.form.processConcurrency = parsed.overlay;
    this.saving = true;
    this.errorMessage = '';
    const operation = this.editing
      ? this.service.orchestrationResourceProfilesProfileIdPut(this.editing.id, this.form)
      : this.service.orchestrationResourceProfilesPost(this.form);
    operation.pipe(
      takeUntil(this.destroy$),
      finalize(() => { this.saving = false; this.cdr.markForCheck(); }),
    ).subscribe({
      next: () => { this.dialogVisible = false; this.load(); },
      error: () => { this.errorMessage = 'Failed to save resource profile'; },
    });
  }

  activate(profile: ResourceProfile): void {
    this.service.orchestrationResourceProfilesProfileIdActivatePost(profile.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.load(),
      error: () => { this.errorMessage = 'Failed to activate resource profile'; this.cdr.markForCheck(); },
    });
  }

  private applyCatalogSearch(): void {
    this.profiles = filterByCatalogQuery(
      this.allProfiles,
      this.searchQuery,
      profile => [profile.name, profile.key, profile.workerGroup, profile.assignmentCapSummary],
    );
    this.emptyListMessage = this.searchQuery.trim() ? 'No catalog matches' : 'No resource profiles found';
  }

  private normalizeQueues(queues: QueueParallelismRequest): QueueParallelismForm {
    return {
      critical: queues.critical ?? 1,
      high: queues.high ?? 1,
      normal: queues.normal ?? 1,
      bulk: queues.bulk ?? 1,
    };
  }

  private defaultForm(): ResourceProfileForm {
    return {
      key: 'satellite-default',
      name: 'Satellite worker default',
      isActive: true,
      copernicusDownloadConcurrency: 1,
      minioReadConcurrency: 1,
      minioWriteConcurrency: 1,
      gdalConcurrency: 1,
      gdalCacheMaxMB: 256,
      downloadQueues: { critical: 2, high: 2, normal: 2, bulk: 1 },
      postProcessQueues: { critical: 1, high: 1, normal: 1, bulk: 1 },
      processConcurrency: {},
    };
  }
}
